/**
 * Threat Intelligence API Service
 * 
 * API service for consuming ChatGPT-powered threat intelligence endpoints.
 * Provides type-safe methods for fetching dynamic threat insights.
 * 
 * All methods support optional query context for targeted analysis.
 */

import axiosInstance from "../../api/axiosInstance";
import {
  DashboardThreatOverview,
  CategoryThreatData,
  ThreatSummaryResponse,
  CategoryComparisonResponse,
  RefreshInsightsResponse,
  ThreatCategoryKey,
  VulnerableCompaniesReport
} from "../../types/threatIntelligence";

const BASE_URL = "/api/dashboard";

// Query context for targeted threat intelligence
export interface QueryContext {
  cveId?: string;
  industry?: string;
  threatType?: string;
  timeRange?: string;
  region?: string;
  keywords?: string[];
}

/**
 * Build query string from context object
 */
function buildQueryString(context?: QueryContext): string {
  if (!context) return "";
  
  const params = new URLSearchParams();
  if (context.cveId) params.append("cveId", context.cveId);
  if (context.industry) params.append("industry", context.industry);
  if (context.threatType) params.append("threatType", context.threatType);
  if (context.timeRange) params.append("timeRange", context.timeRange);
  if (context.region) params.append("region", context.region);
  if (context.keywords?.length) params.append("keywords", context.keywords.join(","));
  
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}

/**
 * Fetch comprehensive threat intelligence overview
 * This is the main endpoint for the dashboard, providing AI-generated insights
 * 
 * @param context - Optional query context for targeted analysis
 */
export async function getThreatInsights(context?: QueryContext): Promise<DashboardThreatOverview> {
  const queryString = buildQueryString(context);
  const response = await axiosInstance.get<DashboardThreatOverview>(
    `${BASE_URL}/threat-insights${queryString}`
  );
  return response.data;
}

/**
 * Fetch threat insights for a specific category
 * 
 * @param category - The threat category to fetch insights for
 * @param context - Optional query context for targeted analysis
 */
export async function getCategoryThreatInsights(
  category: ThreatCategoryKey,
  context?: QueryContext
): Promise<CategoryThreatData> {
  const queryString = buildQueryString(context);
  const response = await axiosInstance.get<CategoryThreatData>(
    `${BASE_URL}/threat-insights/${category}${queryString}`
  );
  return response.data;
}

/**
 * Fetch lightweight threat summary (for widgets/quick views)
 * 
 * @param context - Optional query context for targeted analysis
 */
export async function getThreatSummary(context?: QueryContext): Promise<ThreatSummaryResponse> {
  const queryString = buildQueryString(context);
  const response = await axiosInstance.get<ThreatSummaryResponse>(
    `${BASE_URL}/threat-summary${queryString}`
  );
  return response.data;
}

/**
 * Fetch category comparison data
 * 
 * @param context - Optional query context for targeted analysis
 */
export async function getCategoryComparison(context?: QueryContext): Promise<CategoryComparisonResponse> {
  const queryString = buildQueryString(context);
  const response = await axiosInstance.get<CategoryComparisonResponse>(
    `${BASE_URL}/category-comparison${queryString}`
  );
  return response.data;
}

/**
 * Fetch vulnerable companies report by country
 *
 * @param context - Optional query context for targeted analysis
 */
export async function getVulnerableCompaniesReport(
  context?: QueryContext
): Promise<VulnerableCompaniesReport> {
  const queryString = buildQueryString(context);
  const response = await axiosInstance.get<VulnerableCompaniesReport>(
    `${BASE_URL}/vulnerable-companies${queryString}`
  );
  return response.data;
}

/**
 * Force refresh threat intelligence cache
 * Use sparingly - cache auto-refreshes every 15 minutes
 */
export async function refreshThreatInsights(): Promise<RefreshInsightsResponse> {
  const response = await axiosInstance.post<RefreshInsightsResponse>(
    `${BASE_URL}/refresh-insights`
  );
  return response.data;
}

/**
 * Query threat intelligence with specific context
 * Advanced endpoint for targeted queries with CVE IDs, industry sectors, etc.
 * 
 * @param category - The threat category to query
 * @param context - Query context with specific parameters
 */
export async function queryThreatIntelligence(
  category: ThreatCategoryKey,
  context: QueryContext
): Promise<{ success: boolean; data: CategoryThreatData; queryContext: QueryContext; generatedAt: string }> {
  const response = await axiosInstance.post(
    `${BASE_URL}/query`,
    { category, ...context }
  );
  return response.data;
}

export default {
  getThreatInsights,
  getCategoryThreatInsights,
  getThreatSummary,
  getCategoryComparison,
  getVulnerableCompaniesReport,
  refreshThreatInsights,
  queryThreatIntelligence
};
